import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { formatPhone } from '@chiroup/core/functions/format';
import { Globe as GlobeAltIcon, Map as MapIcon, Phone as PhoneIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { PatientClinicLocation, usePatientPortal } from '../../contexts/PatientPortalContext';

const FacebookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
      clipRule="evenodd"
    />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const YouTubeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 01-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 01-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 011.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418zM15.194 12l-5.197 3V9l5.197 3z"
      clipRule="evenodd"
    />
  </svg>
);

// `clinic.facebook` / `clinic.x` / `clinic.youtube` are free-form text
// — clinics often enter bare handles (`myclinic`, `@myclinic`) or
// domain-only forms (`facebook.com/myclinic`) instead of full URLs.
// Normalize so the `<a href>` always opens.
const normalizeSocialUrl = (value: string, platform: 'facebook' | 'x' | 'youtube'): string => {
  const trimmed = value.trim();

  if (!trimmed) return '';

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (/^www\./i.test(trimmed) || /(facebook|fb|twitter|x|youtube|youtu)\.[a-z]/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  const handle = trimmed.replace(/^@/, '');

  switch (platform) {
    case 'facebook':
      return `https://facebook.com/${handle}`;
    case 'x':
      return `https://x.com/${handle}`;
    case 'youtube':
      return `https://youtube.com/@${handle}`;
  }
};

const formatAddress = (loc: {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string => {
  if (!loc.address1) return '';

  return `${loc.address1}${loc.address2 ? ` ${loc.address2}` : ''}${
    loc.city ? `, ${loc.city}` : ''
  }${loc.state ? ` ${loc.state}` : ''}${loc.zip ? ` ${loc.zip}` : ''}`;
};

type LocationLike = Partial<PatientClinicLocation> & {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  site?: string | null;
  facebook?: string | null;
  x?: string | null;
  youtube?: string | null;
  name?: string | null;
  path?: string | null;
};

// Per-location card. The patient's assigned location renders with a
// brand-color ring so it visually anchors
// the list. Other active locations follow with a neutral card style.
const LocationCard: React.FC<{
  location: LocationLike;
  isAssigned: boolean;
  primaryColor: string;
  language: 'en' | 'es';
  isiOS: boolean;
}> = ({ location, isAssigned, primaryColor, language, isiOS }) => {
  const address = formatAddress(location);
  const addressEncoded = address ? encodeURIComponent(address) : '';
  const directionsHref = address
    ? isiOS
      ? `http://maps.apple.com/?q=${addressEncoded}`
      : `https://www.google.com/maps/dir//${addressEncoded}`
    : '';

  const actions: {
    key: string;
    label: string;
    detail: string;
    actionText: string;
    href: string;
    icon: React.ReactNode;
    newTab?: boolean;
  }[] = [];

  if (location.phone) {
    actions.push({
      key: 'phone',
      label: hcVerbiage.call[language],
      detail: formatPhone(location.phone, true) || '',
      actionText: 'Call',
      href: `tel:${location.phone}`,
      icon: <PhoneIcon className="h-5 w-5" />,
    });
  }

  if (location.address1 && directionsHref) {
    actions.push({
      key: 'directions',
      label: hcVerbiage.directions[language],
      detail: address,
      actionText: 'Get Directions',
      href: directionsHref,
      icon: <MapIcon className="h-5 w-5" />,
      newTab: true,
    });
  }

  if (location.email) {
    actions.push({
      key: 'email',
      label: hcVerbiage.email[language],
      detail: location.email,
      actionText: 'Send Email',
      href: `mailto:${location.email}`,
      icon: <Icon icon={Icons.mail} className="h-5 w-5" />,
    });
  }

  if (location.site) {
    const siteWithoutProtocol = location.site.replace(/^https?:\/\//, '');
    actions.push({
      key: 'site',
      label: hcVerbiage.site[language],
      detail: siteWithoutProtocol,
      actionText: 'Visit',
      href: `https://${siteWithoutProtocol}`,
      icon: <GlobeAltIcon className="h-5 w-5" />,
      newTab: true,
    });
  }

  const socials: { label: string; href: string; icon: React.ReactNode }[] = [];

  if (location.facebook) {
    socials.push({
      label: 'Facebook',
      href: normalizeSocialUrl(location.facebook, 'facebook'),
      icon: <FacebookIcon className="h-5 w-5" />,
    });
  }

  if (location.x) {
    socials.push({
      label: 'X',
      href: normalizeSocialUrl(location.x, 'x'),
      icon: <XIcon className="h-5 w-5" />,
    });
  }

  if (location.youtube) {
    socials.push({
      label: 'YouTube',
      href: normalizeSocialUrl(location.youtube, 'youtube'),
      icon: <YouTubeIcon className="h-5 w-5" />,
    });
  }

  // Highlight assigned with a primary-color ring + tinted background.
  const containerCls = isAssigned
    ? 'rounded-2xl p-4 flex flex-col gap-3 ring-2 dark:ring-1'
    : 'portal-card p-4 flex flex-col gap-3';
  const containerStyle: React.CSSProperties = isAssigned
    ? {
        backgroundColor: `${primaryColor}10`,
        ['--tw-ring-color' as any]: primaryColor,
      }
    : {};

  return (
    <section className={containerCls} style={containerStyle}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            {location.name || 'Location'}
          </h3>
          {location.primary && !isAssigned && (
            <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {language === 'es' ? 'Sede principal' : 'Primary location'}
            </p>
          )}
        </div>
      </header>

      {actions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {actions.map(action => (
            <a
              key={action.key}
              href={action.href}
              target={action.newTab ? '_blank' : undefined}
              rel={action.newTab ? 'noopener noreferrer' : undefined}
              className="portal-card p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
            >
              <div
                className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
              >
                {action.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {action.label}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                  {action.detail}
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold" style={{ color: primaryColor }}>
                {action.actionText}
              </span>
            </a>
          ))}
        </div>
      )}

      {socials.length > 0 && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {language === 'es' ? 'Síguenos' : 'Follow'}
          </p>
          <div className="flex items-center gap-3">
            {socials.map(social => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="flex size-9 items-center justify-center rounded-xl text-white transition-opacity active:opacity-80"
                style={{ backgroundColor: primaryColor }}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const ClinicInfo: React.FC = () => {
  const { selectedPatient, language } = usePatientPortal();
  const clinic = selectedPatient?.clinic;
  const primaryColor = clinic?.primaryColor || '#77bd43';

  const isiOS = useMemo(() => {
    const ua = navigator.userAgent;

    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }, []);

  // Build the list of locations to render. Prefer the backend-supplied
  // `availableLocations` (all active locations for the clinic); fall
  // back to a single synthetic entry built from the patient's clinic
  // record so single-location clinics and any pre-deploy clients still
  // see something useful.
  const locations = useMemo<LocationLike[]>(() => {
    if (!clinic) return [];

    if (clinic.availableLocations?.length) {
      return clinic.availableLocations;
    }

    return [
      {
        ID: 0,
        path: clinic.path || null,
        name: clinic.name || null,
        address1: clinic.address1 || null,
        address2: clinic.address2 || null,
        city: clinic.city || null,
        state: clinic.state || null,
        zip: clinic.zip || null,
        country: clinic.country || null,
        phone: clinic.phone || null,
        email: clinic.email || null,
        site: clinic.site || null,
        facebook: clinic.facebook || null,
        x: clinic.x || null,
        youtube: clinic.youtube || null,
        primary: true,
      },
    ];
  }, [clinic]);

  // The patient's assigned location is the one whose `ID` matches
  // `clinic.locationId` (which mePortal pins from the patient's
  // `location` FK). Matching by `path` was unreliable — locations
  // without a vanity-URL collide on `null`, causing the first
  // pathless location to look "assigned" no matter who you are.
  const assignedLocationId = clinic?.locationId ?? null;
  const orderedLocations = useMemo(() => {
    if (locations.length <= 1) return locations;
    if (assignedLocationId == null) return locations;

    const assignedIdx = locations.findIndex(l => l.ID === assignedLocationId);

    if (assignedIdx <= 0) return locations;

    const assigned = locations[assignedIdx];

    return [assigned, ...locations.filter((_, i) => i !== assignedIdx)];
  }, [locations, assignedLocationId]);

  if (!clinic) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">No clinic information available.</p>
      </div>
    );
  }

  const clinicName = clinic.clinicName || clinic.name || hcVerbiage.myClinic[language];
  const hasMultipleLocations = orderedLocations.length > 1;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{clinicName}</h2>
        {selectedPatient?.primaryClinician && (
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
            {selectedPatient.primaryClinician}
          </p>
        )}
      </div>

      {orderedLocations.map(location => {
        const isAssigned =
          hasMultipleLocations && assignedLocationId != null && location.ID === assignedLocationId;

        return (
          <LocationCard
            key={location.ID ?? location.path ?? location.name ?? Math.random()}
            location={location}
            isAssigned={isAssigned}
            primaryColor={primaryColor}
            language={language}
            isiOS={isiOS}
          />
        );
      })}
    </div>
  );
};

export default ClinicInfo;
